import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ChevronRight, X, Package2, Globe } from 'lucide-react';
import RequestForm from '../services/RequestForm';
import { useLanguage } from '../../contexts/LanguageContext';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  language: string;
  setLanguage: (lang: 'ru' | 'en') => void;
  onLogout: () => void;
}

function MobileMenu({ isOpen, onClose, isAuthenticated, language, setLanguage }: MobileMenuProps) {
  const navigate = useNavigate();
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmenuClick = (menu: string) => {
    setActiveSubmenu(activeSubmenu === menu ? null : menu);
  };

  const renderSubmenu = (menu: string) => {
    switch (menu) {
      case 'services':
        return (
          <div className="bg-[#161617]">
            <div>
              <a href="/services/business/search" className="block px-6 py-3 text-[17px] text-white hover:text-white hover:bg-[#ffffff1a] transition-all">
                {t('services.search')}
              </a>
              <a href="/services/business/logistics" className="block px-6 py-3 text-[17px] text-white hover:text-white hover:bg-[#ffffff1a] transition-all">
                {t('services.logistics')}
              </a>
              <a href="/services/business/forwarding" className="block px-6 py-3 text-[17px] text-white hover:text-white hover:bg-[#ffffff1a] transition-all">
                {t('services.inspection')}
              </a>
              <a href="/services/business/payment" className="block px-6 py-3 text-[17px] text-white hover:text-white hover:bg-[#ffffff1a] transition-all">
                {t('services.payment')}
              </a>
            </div>
          </div>
        );
      case 'prices':
        return (
          <div className="bg-[#161617]">
            <a href="/prices/business" className="block px-6 py-3 text-[17px] text-white hover:text-white hover:bg-[#ffffff1a] transition-all">
              Для бизнеса
            </a>
            <a href="/prices/personal" className="block px-6 py-3 text-[17px] text-white hover:text-white hover:bg-[#ffffff1a] transition-all">
              Для частных лиц
            </a>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 transition-all duration-300 ${
        isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
      }`}
    >
      <div 
        className={`fixed inset-0 bg-[#161617] transform transition-transform duration-500 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="h-12 flex items-center justify-between px-4 bg-[#1d1d1f]/90 backdrop-blur-xl border-b border-[#424245]">
          <div className="flex items-center justify-between w-full">
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-[#f5f5f7] hover:text-white transition-colors rounded-full hover:bg-[#ffffff1a] active:bg-[#ffffff26]"
            >
              <X className="h-5 w-5" />
            </button>
            
            <img 
              src="https://i.ibb.co/t08SkQL/6069.png"
              alt="6069 CARGO"
              className="h-[20px]"
              loading="eager"
            />
            
            {isAuthenticated ? (
              <button
                onClick={() => {
                  navigate('/client/dashboard');
                  onClose();
                }}
                className="w-10 h-10 flex items-center justify-center text-[#f5f5f7] hover:text-white transition-colors rounded-full hover:bg-[#ffffff1a] active:bg-[#ffffff26]"
              >
                <User className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => {
                  navigate('/login'); 
                  onClose();
                }}
                className="w-10 h-10 flex items-center justify-center text-[#f5f5f7] hover:text-white transition-colors rounded-full hover:bg-[#ffffff1a] active:bg-[#ffffff26]"
              >
                <User className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Menu Content */}
        <div className="h-[calc(100%-3rem)] overflow-auto">
          <div className="py-2">
            <button
              onClick={() => handleSubmenuClick('services')}
              className="flex items-center justify-between w-full px-6 py-4 text-[17px] text-[#f5f5f7] hover:text-white hover:bg-[#ffffff0d] transition-all"
            >
              <span>{t('nav.services')}</span>
              <ChevronRight className={`w-5 h-5 transition-transform duration-300 ${
                activeSubmenu === 'services' ? 'rotate-90' : ''
              }`} />
            </button>
            {activeSubmenu === 'services' && renderSubmenu('services')}
            
            <a 
              href="/prices"
              className="block px-6 py-4 text-[17px] text-[#f5f5f7] hover:text-white hover:bg-[#ffffff0d] transition-all"
              onClick={onClose}
            >
              {t('nav.prices')}
            </a>

            <button
              onClick={() => setShowRequestForm(true)}
              className="flex items-center w-full px-6 py-4 text-[17px] text-[#f5f5f7] hover:text-white hover:bg-[#ffffff0d] transition-all"
            >
              <Package2 className="w-5 h-5 mr-2" />
              {t('nav.order')}
            </button>

            <a 
              href="/about"
              className="block px-6 py-4 text-[17px] text-[#f5f5f7] hover:text-white hover:bg-[#ffffff0d] transition-all"
              onClick={onClose}
            >
              {t('nav.about')}
            </a>
            <a 
              href="/contacts"
              className="block px-6 py-4 text-[17px] text-[#f5f5f7] hover:text-white hover:bg-[#ffffff0d] transition-all"
              onClick={onClose}
            >
              {t('nav.contacts')}
            </a>
            
            {/* Language Switcher */}
            <div className="px-6 py-4 border-t border-[#424245]">
              <div className="text-[11px] font-semibold text-[#86868b] mb-2">
                {t('nav.language')}
              </div>
              <div className="space-y-2">
                <button 
                  onClick={() => setLanguage('ru')}
                  className={`w-full text-left text-[17px] ${language === 'ru' ? 'text-white font-medium' : 'text-[#86868b]'} flex items-center`}
                >
                  <Globe className="w-4 h-4 mr-2 text-[#86868b]" />
                  Русский
                </button>
                <button 
                  onClick={() => setLanguage('en')}
                  className={`w-full text-left text-[17px] ${language === 'en' ? 'text-white font-medium' : 'text-[#86868b]'} flex items-center`}
                >
                  <Globe className="w-4 h-4 mr-2 text-[#86868b]" />
                  English
                </button>
                <button
                  onClick={() => setLanguage('zh')}
                  className={`w-full text-left text-[17px] ${language === 'zh' ? 'text-white font-medium' : 'text-[#86868b]'} flex items-center`}
                >
                  <Globe className="w-4 h-4 mr-2 text-[#86868b]" />
                  中文
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Request Form Popup */}
      {showRequestForm && (
        <RequestForm 
          isPopup 
          source={language === 'ru' ? "Мобильное меню" : "Mobile Menu"}
          onClose={() => {
            setShowRequestForm(false);
            // Don't close the mobile menu when closing the form
          }}
        />
      )}
    </div>
  );
}

export default MobileMenu;